import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { tempToRgb01 } from "../constants/thermalConstants";

/** Builds + keeps a vertex-coloured PlaneGeometry in sync with node temperatures. */
export function useVertexColors(
    gridSize: number,
    planeSize: number,
    nodes: Record<string, number>,
    keyPrefix: string,
    globalMin: number,
    globalMax: number,
): THREE.PlaneGeometry {
    const geometry = useMemo(() => {
        const g = new THREE.PlaneGeometry(planeSize, planeSize, gridSize - 1, gridSize - 1);
        g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(gridSize * gridSize * 3), 3));
        return g;
    }, [gridSize, planeSize]);

    useEffect(() => {
        const attr = geometry.getAttribute("color") as THREE.BufferAttribute;
        const c = new THREE.Color();
        for (let i = 0; i < gridSize * gridSize; i++) {
            const [r, g, b] = tempToRgb01(nodes[`${keyPrefix}_N${i + 1}`] ?? 0, globalMin, globalMax);
            c.setRGB(r, g, b, THREE.SRGBColorSpace);
            attr.setXYZ(i, c.r, c.g, c.b);
        }
        attr.needsUpdate = true;
    }, [nodes, keyPrefix, gridSize, globalMin, globalMax, geometry]);

    return geometry;
}

/** Computes the local (x, y) centre position for every cell in an n×n grid. */
export function useCellPositions(
    nodesPerFace: number,
    gridSize: number,
    cellSize: number,
): { ni: number; x: number; y: number }[] {
    return useMemo(
        () =>
            Array.from({ length: nodesPerFace }, (_, ni) => ({
                ni,
                x: (ni % gridSize - (gridSize - 1) / 2) * cellSize,
                y: ((gridSize - 1) / 2 - Math.floor(ni / gridSize)) * cellSize,
            })),
        [nodesPerFace, gridSize, cellSize],
    );
}

/**
 * Builds a BufferGeometry of line segments for an n×n grid of the given square size.
 *
 * - `includeOuter = true`  — all (gridSize + 1) lines in each axis: outer border + inner dividers
 * - `includeOuter = false` — only the (gridSize - 1) inner divider lines; no outer border
 *
 * Returns null when `gridSize <= 1` and `includeOuter` is false (nothing to draw).
 */
export function useGridLinesGeo(
    gridSize: number,
    size: number,
    includeOuter: boolean,
): THREE.BufferGeometry | null {
    return useMemo(() => {
        const start = includeOuter ? 0 : 1;
        const end   = includeOuter ? gridSize : gridSize - 1;
        if (start > end) return null;

        const verts: number[] = [];
        const step = size / gridSize;
        const half = size / 2;

        for (let i = start; i <= end; i++) {
            const t = i * step - half;
            verts.push(-half, t, 0,  half, t, 0); // horizontal
            verts.push(t, -half, 0,  t,  half, 0); // vertical
        }

        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(verts), 3));
        return g;
    }, [gridSize, size, includeOuter]);
}

/** Throttle a value so it only updates at most once every `intervalMs`. */
export function useThrottledValue<T>(value: T, intervalMs: number): T {
    const [throttledValue, setThrottledValue] = useState(value);
    const lastUpdateRef = useRef<number>(0);
    const timeoutIdRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = undefined;
        }

        if (intervalMs <= 0) {
            lastUpdateRef.current = performance.now();
            setThrottledValue(value);
            return;
        }

        const now = performance.now();
        const timeSinceLastUpdate = now - lastUpdateRef.current;

        if (timeSinceLastUpdate >= intervalMs) {
            lastUpdateRef.current = now;
            setThrottledValue(value);
        } else {
            const timeUntilNextUpdate = intervalMs - timeSinceLastUpdate;
            timeoutIdRef.current = setTimeout(() => {
                lastUpdateRef.current = performance.now();
                setThrottledValue(value);
                timeoutIdRef.current = undefined;
            }, timeUntilNextUpdate);
        }
    }, [value, intervalMs]);

    return throttledValue;
}

// ── Synced Camera State ────────────────────────────────────────────────────────

interface CameraState {
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; order: string };
  zoom: number;
}

/**
 * Manages shared camera state for synced dual-view rendering
 * When enabled, both canvases share the same camera transformation (position, rotation, zoom)
 */
export const useSyncedCameraState = (enabled: boolean) => {
  // Track syncing state
  const syncingRef = useRef<string | null>(null);
  const lastSyncRef = useRef<number>(0);

  // Shared state for camera transformation (using plain objects for stability)
  const cameraState = useRef<CameraState>({
    position: { x: 0.5, y: 1.8, z: 2.4 },
    rotation: { x: 0, y: 0, z: 0, order: 'YXZ' },
    zoom: 1,
  });

  // Reusable temporary objects to avoid allocations
  const tmpVector = useRef(new THREE.Vector3());
  const camerasRef = useRef<Map<string, { camera: THREE.Camera; invalidate: () => void }>>(new Map());

  // Apply shared state to a camera - wrapped in useCallback for consistent reference
  const applyStateToCamera = useCallback((camera: THREE.Camera | undefined, state: CameraState) => {
    if (!camera) return;
    camera.position.set(state.position.x, state.position.y, state.position.z);
    tmpVector.current.set(state.rotation.x, state.rotation.y, state.rotation.z);
    camera.rotation.setFromVector3(tmpVector.current, state.rotation.order as any);

    if (camera instanceof THREE.PerspectiveCamera) {
      camera.zoom = state.zoom;
      camera.updateProjectionMatrix();
    }

    camera.updateMatrix();
    camera.updateMatrixWorld(true);
  }, []);

  // Register a camera with the sync system
  const registerCamera = useCallback(
    (id: string, camera: THREE.Camera, invalidate: () => void) => {
      camerasRef.current.set(id, { camera, invalidate });
      // Apply current shared state to newly registered camera
      if (enabled) {
        applyStateToCamera(camera, cameraState.current);
      }
    },
    [enabled, applyStateToCamera]
  );

  // Unregister a camera
  const unregisterCamera = useCallback((id: string) => {
    camerasRef.current.delete(id);
  }, []);

  // Update shared state from one camera and apply to all others
  const syncCameraState = useCallback(
    (sourceId: string) => {
      // Throttle syncs to prevent excessive updates (16ms = ~60fps)
      const now = performance.now();
      if (now - lastSyncRef.current < 16) return;
      lastSyncRef.current = now;

      // Prevent feedback loops
      if (syncingRef.current) return;

      const sourceEntry = camerasRef.current.get(sourceId);
      if (!sourceEntry) return;

      try {
        syncingRef.current = sourceId;

        // Extract and update source camera state (reuse current object)
        const sourceCamera = sourceEntry.camera;
        const state = cameraState.current;
        state.position.x = sourceCamera.position.x;
        state.position.y = sourceCamera.position.y;
        state.position.z = sourceCamera.position.z;
        state.rotation.x = sourceCamera.rotation.x;
        state.rotation.y = sourceCamera.rotation.y;
        state.rotation.z = sourceCamera.rotation.z;
        state.rotation.order = sourceCamera.rotation.order;

        if (sourceCamera instanceof THREE.PerspectiveCamera) {
          state.zoom = sourceCamera.zoom;
        }

        // Apply to all other cameras and invalidate them for re-render
        camerasRef.current.forEach(({ camera, invalidate }, id) => {
          if (id !== sourceId) {
            applyStateToCamera(camera, state);
            invalidate();
          }
        });
      } finally {
        syncingRef.current = null;
      }
    },
    [applyStateToCamera]
  );

  return useMemo(
    () => ({ enabled, registerCamera, unregisterCamera, syncCameraState }),
    [enabled, registerCamera, unregisterCamera, syncCameraState]
  );
};

// ── Smoothed body rotation ─────────────────────────────────────────────────────

const BODY_TIME_CONSTANT_SEC = 0.3;       // how quickly the body settles onto a new target
const BODY_MAX_STEP_SEC = 0.1;            // clamp long frame gaps so the body does not jump after idling
const BODY_SETTLE_RAD = 1e-3;             // below this angle the body counts as settled
const BODY_REDRAW_INTERVAL_MS = 1000 / 30; // redraw cap while the body is still moving
const BODY_DEFAULT_QUATERNION = new THREE.Quaternion();

/**
 * Damped follow for the spacecraft body group (use inside the Canvas). Returns a ref for the group whose
 * rotation eases toward `target` each frame. It snaps when `snapKey` changes (time jumps), and requests
 * redraws at most about 30 times per second while it is still moving, so the canvas stays idle once settled.
 */
export function useSmoothedBodyRotation(target: THREE.Quaternion | undefined, snapKey: number) {
    const { invalidate } = useThree();
    const groupRef = useRef<THREE.Group>(null);
    const goalRef = useRef(new THREE.Quaternion());
    const snapKeyRef = useRef(snapKey);
    const initializedRef = useRef(false);
    const redrawTimerRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        goalRef.current.copy(target ?? BODY_DEFAULT_QUATERNION);
        const group = groupRef.current;
        if (!group) return;
        if (!initializedRef.current || snapKey !== snapKeyRef.current) {
            // First mount or a time jump: copy straight across
            initializedRef.current = true;
            snapKeyRef.current = snapKey;
            group.quaternion.copy(goalRef.current);
        }
        invalidate();
    }, [target, snapKey, invalidate]);

    useEffect(() => () => window.clearTimeout(redrawTimerRef.current), []);

    useFrame((_, delta) => {
        const group = groupRef.current;
        if (!group) return;
        const goal = goalRef.current;
        if (group.quaternion.angleTo(goal) < BODY_SETTLE_RAD) {
            group.quaternion.copy(goal);
            return; // settled: stop scheduling redraws
        }
        const alpha = 1 - Math.exp(-Math.min(delta, BODY_MAX_STEP_SEC) / BODY_TIME_CONSTANT_SEC);
        group.quaternion.slerp(goal, alpha);
        window.clearTimeout(redrawTimerRef.current);
        redrawTimerRef.current = window.setTimeout(() => invalidate(), BODY_REDRAW_INTERVAL_MS);
    });

    return groupRef;
}
